import { useMemo, useState } from 'react';

import clsx from 'clsx';

import { TranslationService } from '@/utils/services/TranslationService';

import useLibraryMedia from '@/utils/hooks/swr/useLibraryMedia';
import useTranslation from '@/utils/hooks/translation/useTranslation';
import useActiveSocialProfile from '@/utils/hooks/user/useActiveSocialProfile';

import useToggle from '@/utils/hooks/utils/useToggle';

// Hooks
import { MEDIA_LIBRARY } from '@/lib/routes/privateRoutes';

import InfoBanner from '@/components/base/InfoBanner';
import LoadMoreInfinite from '@/components/base/LoadMoreInfinite/LoadMoreInfinite';
import SkeletonList from '@/components/base/Skeleton/SkeletonList';
import { Illustration } from '@/components/composite/Illustration';
import { MediaGrid } from '@/components/layouts/wrappers/MediaGrid';
import { WrapperSwitchLayout } from '@/components/layouts/wrappers/WrapperSwitchLayout';
import Filters from '@/components/pages/media/Filters';
import { HeaderMediaLibrary } from '@/components/pages/media/HeaderMediaLibrary';
import HeadingActions from '@/components/pages/media/HeadingActions';
import MediaLibraryViewerModal from '@/components/pages/media/MediaLibraryViewer/MediaLibraryViewerModal';
import DeleteMediaModal from '@/components/pages/media/modals/DeleteMediaModal';
// Layouts
import { PageContentWrapper } from '@/components/layouts/LayoutPlatform/components/PageContentWrapper';
// Components
import MediaCard from '@/components/pages/media/components/MediaCard';

//image
import illustrationEn from './../../../public/images/illustrations/illustrations-empty-en.webp';
import illustrationEs from './../../../public/images/illustrations/illustrations-empty-es.webp';
import illustrationFr from './../../../public/images/illustrations/illustrations-empty-fr.webp';

const defaultParams = {
    media_types: [],
    tags_ids: [],
    source: undefined,
    added_at: undefined
};

const PageContent = () => {
    const { t } = useTranslation('media');

    const [selectedIds, setSelected] = useState([]);
    const { isOpen: isFiltersOpen, toggle: toggleFilters } = useToggle();
    const { isOpen: isDeleteModalOpen, toggle: toggleDeleteModal } = useToggle();

    const [MLVState, setMLVState] = useState({
        isOpen: false,
        currentMediaIndex: null
    });

    const { activeSocialProfile } = useActiveSocialProfile();
    const {
        dataLibraryMedia,
        medias,
        isLoadingInitialData,
        mutateLibraryMedia,
        isLoadingMore,
        isLoadMore,
        loadMoreLibraryMedia,
        libraryMediaError,
        requestParams,
        setRequestParams
    } = useLibraryMedia({
        activeSPID: activeSocialProfile?.id,
        limit: 5,
        params: defaultParams
    });

    const handleSelectMultiple = () => {
        if (selectedIds.length === 0) {
            setSelected(medias.map((media) => media.id));
        } else {
            setSelected([]);
        }
    };

    const handleSelectOne = (id) => {
        if (selectedIds.some((selectedId) => selectedId === id)) {
            setSelected((ids) => ids.filter((el) => el !== id));
        } else {
            setSelected((ids) => [...ids, id]);
        }
    };

    const onNavigate = (mediaId) => {
        setMLVState((prevState) => ({
            ...prevState,
            currentMediaIndex: medias.findIndex((mediaInList) => mediaInList.id === mediaId)
        }));
    };

    const deleteSuccess = (deletedMediaIds) => {
        if (deletedMediaIds.length === selectedIds.length) {
            setSelected([]);
        } else {
            setSelected((prevState) => prevState.filter((id) => !deletedMediaIds.includes(id)));
        }
        mutateLibraryMedia();
    };

    const inCommonTags = useMemo(() => {
        let allTags = [];
        medias
            .filter((media) => selectedIds.includes(media.id))
            .map((media) => {
                if (media.tags) {
                    allTags = [...allTags, ...media.tags];
                }
            });

        let commonTags = [];

        allTags.map((tag) => {
            if (
                allTags.filter((t) => t.id === tag.id).length >= selectedIds.length &&
                commonTags.filter((t) => tag.id === t.id).length === 0
            ) {
                commonTags.push(tag);
            }
        });

        return commonTags;
    }, [medias, selectedIds]);

    let availableTags = [];
    if (dataLibraryMedia) {
        availableTags = dataLibraryMedia[0].available_tags;
    }
    return (
        <>
            <HeaderMediaLibrary
                dataLibraryMedia={dataLibraryMedia}
                mutateLibraryMedia={mutateLibraryMedia}
                isFiltersOpen={isFiltersOpen}
                toggleFilters={toggleFilters}
            />
            <PageContentWrapper>
                <div className="flex flex-1 flex-col">
                    {libraryMediaError ? (
                        <InfoBanner
                            className="mb-8"
                            title={tErrors('error')}
                            message={tErrors('error_occured_fetch_data')}
                        />
                    ) : null}

                    <HeadingActions
                        isLoading={isLoadingInitialData || isLoadingMore}
                        nbMediaVisible={medias.length}
                        selectedIds={selectedIds}
                        onChange={handleSelectMultiple}
                        mutateLibraryMedia={mutateLibraryMedia}
                        isFiltersOpen={isFiltersOpen}
                        toggleDeleteModal={toggleDeleteModal}
                        toggleFilters={toggleFilters}
                        availableTags={availableTags}
                        inCommonTags={inCommonTags}
                    />
                    <div className="flex flex-1 flex-col sm:flex-row-reverse">
                        {isFiltersOpen ? (
                            <Filters
                                defaultParams={defaultParams}
                                toggleFilters={toggleFilters}
                                availableTags={availableTags}
                                requestParams={requestParams}
                                setRequestParams={setRequestParams}
                                isLoading={isLoadingInitialData || isLoadingMore}
                            />
                        ) : null}
                        <div
                            className={clsx('flex-1 pt-10', {
                                'hidden @xl/wrapper-platform-content:block': isFiltersOpen
                            })}>
                            <MediaGrid>
                                {!isLoadingInitialData ? (
                                    <>
                                        {medias?.map((media, index) => (
                                            <MediaCard
                                                key={media.id}
                                                position={index}
                                                media={media}
                                                availableTags={availableTags}
                                                checked={selectedIds.some((id) => id === media.id)}
                                                onSelect={handleSelectOne}
                                                mutateLibraryMedia={mutateLibraryMedia}
                                                toggleMVL={() => {
                                                    setMLVState({
                                                        isOpen: true,
                                                        currentMediaIndex: medias.findIndex(
                                                            (mediaInList) =>
                                                                mediaInList.id === media.id
                                                        )
                                                    });
                                                }}
                                            />
                                        ))}
                                        {isLoadMore && (
                                            <div className="col-span-full">
                                                <LoadMoreInfinite
                                                    loadMore={loadMoreLibraryMedia}
                                                    isLoadingMore={isLoadingMore}
                                                />
                                            </div>
                                        )}
                                    </>
                                ) : (
                                    <SkeletonList className="square" type="square" count={10} />
                                )}
                            </MediaGrid>
                            {medias.length === 0 && (
                                <div className="flex h-full w-full flex-col items-center justify-center space-y-2 text-icoGray-500 dark:text-icoDarkMode-beton">
                                    <Illustration
                                        src={{
                                            en: illustrationEn,
                                            fr: illustrationFr,
                                            es: illustrationEs
                                        }}
                                    />

                                    <span>{t('no_media')}</span>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </PageContentWrapper>
            <MediaLibraryViewerModal
                open={MLVState.isOpen}
                availableTags={availableTags}
                onDismiss={() => {
                    setMLVState((prevState) => ({ ...prevState, isOpen: false }));
                }}
                mediaPrev={
                    MLVState.currentMediaIndex !== 0 ? medias[MLVState.currentMediaIndex - 1] : null
                }
                mediaNext={
                    MLVState.currentMediaIndex !== medias.length - 1
                        ? medias[MLVState.currentMediaIndex + 1]
                        : null
                }
                current={medias[MLVState.currentMediaIndex]}
                navAction={onNavigate}
                mutateLibraryMedia={mutateLibraryMedia}
                deleteSuccess={deleteSuccess}
                toggleModal={() => {
                    setMLVState((prevState) => ({ ...prevState, isOpen: false }));
                }}
            />
            <DeleteMediaModal
                isOpen={isDeleteModalOpen}
                toggle={toggleDeleteModal}
                mediaIds={selectedIds}
                deleteSuccess={deleteSuccess}
            />
        </>
    );
};

const MediaLibrary = () => (
    <WrapperSwitchLayout>
        <PageContent />
    </WrapperSwitchLayout>
);

export const getStaticProps = TranslationService.getTranslationMessagesFromRoute(MEDIA_LIBRARY);

export default MediaLibrary;
